import { LegalLayout } from '../components/legal/LegalLayout';

/**
 * BestAcco Privacy Policy — original draft content. Comprehensive coverage,
 * written to reflect what the platform actually collects and shares (guest
 * accounts, bookings, payments via processors, channel distribution, cookies,
 * analytics). US-based processing. Pending review by counsel.
 */
export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="June 29, 2026">
      <p>
        This Privacy Policy explains how <strong>BestAcco LLC</strong>{' '}
        (“BestAcco,” “we,” “us,” or “our”) collects, uses, shares, and protects
        information when you use the BestAcco website at{' '}
        <a href="https://bestacco.com">bestacco.com</a> and the related services
        we provide there (the “Site”). By using the Site, you agree to the
        practices described in this Policy. If you do not agree, please do not use
        the Site.
      </p>

      <h2>1. Scope and independent properties</h2>
      <p>
        This Policy covers information BestAcco collects and processes through the
        Site. The hotels and properties listed on the Site (“Properties”) are
        independently owned and operated. When you book, we share the information
        necessary to fulfil your reservation with the relevant Property, which
        then handles your information under its own privacy practices and as the
        merchant for your stay. We are not responsible for the independent privacy
        practices of Properties or other third parties.
      </p>

      <h2>2. Children’s privacy</h2>
      <p>
        The Site is intended for adults. We do not knowingly collect personal
        information from anyone under 18. If you believe a minor has provided us
        personal information, please contact us and we will take reasonable steps
        to delete it.
      </p>

      <h2>3. Information we collect</h2>
      <h3>3.1 Information you provide</h3>
      <ul>
        <li>
          contact and identity details — such as your name, email address, and
          phone number;
        </li>
        <li>
          booking details — such as travel dates, number of guests, room
          preferences, and special requests;
        </li>
        <li>
          account information — if you create a guest account, your login
          credentials, saved preferences, and booking history; and
        </li>
        <li>
          communications — the content of messages, reviews, or support requests
          you send us.
        </li>
      </ul>
      <h3>3.2 Payment information</h3>
      <p>
        Payments are processed by third-party payment providers. We receive only
        the limited details needed to confirm and reconcile a transaction; we do
        not collect or store full payment-card numbers ourselves.
      </p>
      <h3>3.3 Information collected automatically</h3>
      <p>
        When you use the Site, we and our service providers automatically collect
        certain technical information through cookies and similar technologies,
        such as your device and browser type, operating system, IP address,
        general location derived from your IP address, pages viewed, referring
        pages, and dates and times of access.
      </p>
      <h3>3.4 Information from other sources</h3>
      <p>
        We may receive information about you from the Properties you book and from
        our service providers and distribution partners, which we combine with the
        information described above to operate the Site and service your bookings.
      </p>

      <h2>4. How we use information</h2>
      <ul>
        <li>to provide the Site and to process, confirm, and manage your bookings;</li>
        <li>to communicate with you about bookings, your account, and support;</li>
        <li>
          to personalize and improve the Site, understand how it is used, and
          develop new features;
        </li>
        <li>to secure the Site and detect, prevent, and address fraud and abuse;</li>
        <li>
          to send you service messages and, where permitted, marketing about
          offers and updates (you can opt out at any time); and
        </li>
        <li>to comply with our legal obligations and enforce our terms.</li>
      </ul>

      <h2>5. How we share information</h2>
      <p>
        We do not sell your personal information. We share it only as needed to
        run the service, including with:
      </p>
      <ul>
        <li>
          <strong>The Property you book</strong> — so it can honor and service
          your reservation;
        </li>
        <li>
          <strong>Payment processors</strong> — to complete and secure
          transactions;
        </li>
        <li>
          <strong>Channel and distribution partners</strong> — where a booking is
          coordinated through a channel manager or online travel agency, the
          information necessary to confirm and manage that booking;
        </li>
        <li>
          <strong>Service providers</strong> — vendors who host, support, analyze,
          or help us operate the Site, who are permitted to use the information
          only to provide services to us and under appropriate confidentiality
          obligations;
        </li>
        <li>
          <strong>Legal, safety, and compliance</strong> — when we believe in good
          faith that disclosure is required by law or legal process, or is
          necessary to protect the rights, property, or safety of BestAcco, our
          users, or the public; and
        </li>
        <li>
          <strong>Business transfers</strong> — in connection with a merger,
          acquisition, financing, or sale of assets, information may be
          transferred as part of that transaction.
        </li>
      </ul>

      <h2>6. Cookies and similar technologies</h2>
      <p>
        We use cookies and similar technologies (such as pixels and local
        storage) to keep you signed in, remember your preferences, enable the
        booking flow across multiple pages, measure and analyze usage, and improve
        the Site. “Session” cookies expire when you close your browser;
        “persistent” cookies remain until they expire or you delete them. You can
        usually control cookies through your browser settings, but disabling some
        cookies may affect how parts of the Site function. Some browsers offer a
        “Do Not Track” signal; because there is no common industry standard for
        responding to it, we do not currently respond to such signals, but you can
        still manage cookies as described above.
      </p>

      <h2>7. Analytics</h2>
      <p>
        We may use third-party analytics services to understand how visitors use
        the Site. These providers may set their own cookies and collect usage
        information (such as IP address and pages viewed) to compile reports for
        us. We use this information in aggregate to improve the Site.
      </p>

      <h2>8. Your choices and privacy rights</h2>
      <p>
        You can opt out of marketing emails at any time using the unsubscribe link
        in those emails or by contacting us; we will still send you transactional
        messages related to your bookings and account. Depending on where you
        live, you may also have rights to request access to, correction of,
        deletion of, or a copy of your personal information, and to object to or
        restrict certain processing. To exercise any of these rights, contact us
        using the details below. We may need to verify your identity before acting
        on a request, and we will respond within the time required by applicable
        law. We will not discriminate against you for exercising your rights.
      </p>

      <h2>9. How to request deletion</h2>
      <p>
        You can request deletion of your personal information by emailing us at
        the address below. To protect your information, we may ask you to verify
        your identity and to confirm the details of your request before we act on
        it. Note that we may retain certain information where we are required or
        permitted to do so by law (for example, records of completed
        transactions).
      </p>

      <h2>10. Data retention</h2>
      <p>
        We keep personal information for as long as needed to provide the Site,
        fulfil the purposes described in this Policy, and meet our legal,
        accounting, and reporting obligations. When information is no longer
        needed, we delete or anonymize it.
      </p>

      <h2>11. Security</h2>
      <p>
        We use reasonable administrative, technical, and organizational measures
        designed to protect personal information, including encryption in transit
        for sensitive data. No method of transmission or storage is completely
        secure, however, and we cannot guarantee absolute security. You are
        responsible for keeping your account credentials confidential.
      </p>

      <h2>12. International users and data location</h2>
      <p>
        BestAcco LLC is based in the United States, and the information we collect
        is processed and stored in the United States and potentially in other
        countries where we or our service providers operate. These countries may
        have data-protection laws that differ from those where you live. By using
        the Site and submitting your information, you understand that it may be
        transferred to and processed in those locations.
      </p>

      <h2>13. Third-party links</h2>
      <p>
        The Site may link to websites and services we do not operate, including
        Properties and partners. This Policy does not apply to those third-party
        sites, and we encourage you to review their privacy notices before
        providing them information.
      </p>

      <h2>14. Changes to this Policy</h2>
      <p>
        We may update this Policy from time to time. We will revise the “Last
        updated” date above and, for material changes, provide a more prominent
        notice. Your continued use of the Site after a change takes effect means
        you accept the updated Policy.
      </p>

      <h2>15. Complaints</h2>
      <p>
        If you have a concern about how we handle your personal information, please
        contact us first using the details below, and we will work with you to
        address it. You may also have the right to lodge a complaint with the data
        protection authority in your jurisdiction.
      </p>

      <h2>16. Contact us</h2>
      <p>
        For questions about this Policy or to exercise your privacy rights,
        contact our privacy contact at:
      </p>
      <p>
        BestAcco LLC<br />
        Attn: Prateek Juneja, Privacy Contact<br />
        99 Free Henry Ford Road, Madisonville, KY 42431, USA<br />
        <a href="mailto:prateek.juneja@bestacco.com">prateek.juneja@bestacco.com</a>
      </p>
    </LegalLayout>
  );
}
